// Renamed from UserGroupConfig.tsx
import * as React from "react";
import { useEffect, useState } from "react";
import {
  ApiUser,
  ApiUserGroup,
  getUserGroups,
  getUsers,
  addUserGroup,
  updateUserGroup,
  deleteUserGroup,
  addPermission,
  deletePermission,
  getDocumentTypePermissionsByGroup,
  ApiPermission,
  getDocumentTypes,
} from "@api/index";
import {
  getPermissionTemplates,
  ApiPermissionTemplate,
} from "@api/permissionTemplates";
import { http } from "@api/http";

interface GroupFormState {
  groupName: string;
  groupDescription: string;
  groupIsDomain: boolean;
  userIds: number[];
  documentTypeIds: number[];
}

const emptyGroup: GroupFormState = {
  groupName: "",
  groupDescription: "",
  groupIsDomain: false,
  userIds: [],
  documentTypeIds: [],
};
const OBJECTTYPE_DOCUMENTTYPE = 2;

export default function UserGroupAdmin() {
  const [groups, setGroups] = useState<ApiUserGroup[]>([]);
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [docTypes, setDocTypes] = useState<any[]>([]);
  const [groupPermissions, setGroupPermissions] = useState<ApiPermission[]>([]);
  const [permissionTemplates, setPermissionTemplates] = useState<
    ApiPermissionTemplate[]
  >([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<GroupFormState>(emptyGroup);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAvailable, setSelectedAvailable] = useState<number[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<number[]>([]);
  const [filterAvail, setFilterAvail] = useState("");
  const [filterMembers, setFilterMembers] = useState("");
  const [selectedDocAvail, setSelectedDocAvail] = useState<number[]>([]);
  const [selectedDocAssigned, setSelectedDocAssigned] = useState<number[]>([]);
  const [filterDocAvail, setFilterDocAvail] = useState("");
  const [filterDocAssigned, setFilterDocAssigned] = useState("");

  useEffect(() => {
    initialLoad();
  }, []);
  async function initialLoad() {
    setLoading(true);
    setError(null);
    try {
      const [g, u, dt, tmpl] = await Promise.all([
        getUserGroups(),
        getUsers(),
        getDocumentTypes(),
        getPermissionTemplates(),
      ]);
      setGroups(g);
      setUsers(u);
      setDocTypes(dt || []);
      setPermissionTemplates(tmpl || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }
  async function fetchGroupPermissions(groupId: number) {
    try {
      const perms = await getDocumentTypePermissionsByGroup(groupId);
      setGroupPermissions(perms);
      return perms;
    } catch {
      setGroupPermissions([]);
      return [];
    }
  }
  function pickBestPermissionTemplate(): number | null {
    if (!permissionTemplates.length) return null;
    const full = permissionTemplates.find(
      (t) => (t.fullControl ?? (t as any).FullControl) === true,
    );
    if (full) return full.id;
    let best: ApiPermissionTemplate | undefined;
    let bestScore = -1;
    permissionTemplates.forEach((t) => {
      const score =
        (t.canRead || (t as any).CanRead ? 1 : 0) +
        (t.canWrite || (t as any).CanWrite ? 1 : 0) +
        (t.canModify || (t as any).CanModify ? 1 : 0) +
        (t.canDelete || (t as any).CanDelete ? 1 : 0);
      if (score > bestScore) {
        bestScore = score;
        best = t;
      }
    });
    return best ? best.id : null;
  }

  const selectedGroup = groups.find((g) => g.id === selectedGroupId) || null;
  async function initFormForGroup(group: ApiUserGroup | null) {
    if (!group) {
      setForm(emptyGroup);
      setGroupPermissions([]);
      return;
    }
    const perms = await fetchGroupPermissions(group.id);
    const memberIds = users
      .filter((u) => u.userGroups?.includes(group.id))
      .map((u) => u.id);
    const docTypeIds = perms
      .filter((p) => p.objectTypeId === OBJECTTYPE_DOCUMENTTYPE)
      .map((p) => p.objectId);
    setForm({
      groupName: group.groupName || "",
      groupDescription: group.groupDescription || "",
      groupIsDomain: group.groupIsDomain || false,
      userIds: memberIds,
      documentTypeIds: docTypeIds,
    });
    resetSelections();
  }
  function resetSelections() {
    setSelectedAvailable([]);
    setSelectedMembers([]);
    setFilterAvail("");
    setFilterMembers("");
    setSelectedDocAvail([]);
    setSelectedDocAssigned([]);
    setFilterDocAvail("");
    setFilterDocAssigned("");
  }
  function handleSelect(id: number) {
    setSelectedGroupId(id);
    setEditing(false);
    initFormForGroup(groups.find((g) => g.id === id) || null);
  }
  function handleAdd() {
    setSelectedGroupId(null);
    setEditing(true);
    initFormForGroup(null);
  }
  function handleEdit() {
    if (selectedGroup) setEditing(true);
  }
  async function handleDelete() {
    if (selectedGroupId == null) return;
    if (!window.confirm("Delete this group?")) return;
    setLoading(true);
    setError(null);
    try {
      await deleteUserGroup(selectedGroupId);
      await initialLoad();
      setSelectedGroupId(null);
      setEditing(false);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }
  function handleFormChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setForm(
      (prev) =>
        ({ ...prev, [name]: type === "checkbox" ? checked : value }) as any,
    );
  }
  function moveSelectedToMembers() {
    if (!selectedAvailable.length) return;
    setForm((p) => ({
      ...p,
      userIds: Array.from(new Set([...p.userIds, ...selectedAvailable])),
    }));
    setSelectedAvailable([]);
  }
  function moveAllToMembers() {
    const availAll = users
      .filter((u) => !form.userIds.includes(u.id))
      .map((u) => u.id);
    setForm((p) => ({
      ...p,
      userIds: Array.from(new Set([...p.userIds, ...availAll])),
    }));
    setSelectedAvailable([]);
  }
  function moveSelectedToAvailable() {
    if (!selectedMembers.length) return;
    setForm((p) => ({
      ...p,
      userIds: p.userIds.filter((id) => !selectedMembers.includes(id)),
    }));
    setSelectedMembers([]);
  }
  function moveAllToAvailable() {
    setForm((p) => ({ ...p, userIds: [] }));
    setSelectedMembers([]);
  }
  function moveDocSelectedToAssigned() {
    if (!selectedDocAvail.length) return;
    setForm((p) => ({
      ...p,
      documentTypeIds: Array.from(
        new Set([...p.documentTypeIds, ...selectedDocAvail]),
      ),
    }));
    setSelectedDocAvail([]);
  }
  function moveDocAllToAssigned() {
    const availAll = docTypes
      .filter((dt) => !form.documentTypeIds.includes(dt.id))
      .map((dt) => dt.id);
    setForm((p) => ({
      ...p,
      documentTypeIds: Array.from(new Set([...p.documentTypeIds, ...availAll])),
    }));
    setSelectedDocAvail([]);
  }
  function moveDocSelectedToAvailable() {
    if (!selectedDocAssigned.length) return;
    setForm((p) => ({
      ...p,
      documentTypeIds: p.documentTypeIds.filter(
        (id) => !selectedDocAssigned.includes(id),
      ),
    }));
    setSelectedDocAssigned([]);
  }
  function moveDocAllToAvailable() {
    setForm((p) => ({ ...p, documentTypeIds: [] }));
    setSelectedDocAssigned([]);
  }
  async function handleSave() {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        groupName: form.groupName,
        groupDescription: form.groupDescription,
        groupIsDomain: form.groupIsDomain,
      } as Omit<ApiUserGroup, "id">;
      let groupId = selectedGroupId;
      if (groupId == null) {
        const newGroup = await addUserGroup(payload);
        groupId = newGroup.id;
        await syncMembership(groupId, [], form.userIds);
        await syncDocumentTypes(groupId, [], form.documentTypeIds);
      } else {
        const beforeUsers = users
          .filter((u) => u.userGroups?.includes(groupId!))
          .map((u) => u.id);
        const beforeDocTypes = groupPermissions
          .filter((p) => p.objectTypeId === OBJECTTYPE_DOCUMENTTYPE)
          .map((p) => p.objectId);
        await updateUserGroup(groupId, payload);
        await Promise.all([
          syncMembership(groupId, beforeUsers, form.userIds),
          syncDocumentTypes(groupId, beforeDocTypes, form.documentTypeIds),
        ]);
      }
      await initialLoad();
      setSelectedGroupId(groupId!);
      setEditing(false);
      await fetchGroupPermissions(groupId!);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }
  async function syncMembership(
    groupId: number,
    oldMembers: number[],
    newMembers: number[],
  ) {
    const toAdd = newMembers.filter((id) => !oldMembers.includes(id));
    const toRemove = oldMembers.filter((id) => !newMembers.includes(id));
    const impacted = users.filter(
      (u) => toAdd.includes(u.id) || toRemove.includes(u.id),
    );
    for (const u of impacted) {
      const current = new Set(u.userGroups || []);
      if (toAdd.includes(u.id)) current.add(groupId);
      if (toRemove.includes(u.id)) current.delete(groupId);
      const updated = { ...u, userGroups: Array.from(current) };
      await http(`/api/user/${u.id}`, {
        method: "PUT",
        json: updated,
        auth: true,
      });
    }
  }
  async function syncDocumentTypes(
    groupId: number,
    oldDocTypes: number[],
    newDocTypes: number[],
  ) {
    const toAdd = newDocTypes.filter((id) => !oldDocTypes.includes(id));
    const toRemove = oldDocTypes.filter((id) => !newDocTypes.includes(id));
    const bestTemplateId = pickBestPermissionTemplate();
    for (const dtId of toAdd)
      await addPermission({
        groupId,
        objectId: dtId,
        objectTypeId: OBJECTTYPE_DOCUMENTTYPE,
        permissionTemplateId: bestTemplateId ?? 0,
      });
    for (const dtId of toRemove) {
      const perm = groupPermissions.find(
        (p) =>
          p.groupId === groupId &&
          p.objectTypeId === OBJECTTYPE_DOCUMENTTYPE &&
          p.objectId === dtId,
      );
      if (perm) await deletePermission(perm.id);
    }
    await fetchGroupPermissions(groupId);
  }
  const inputStyle: React.CSSProperties = {
    padding: "4px",
    fontSize: "1em",
    borderRadius: 4,
    border: "1px solid #cfd8dc",
    background: "#f7fafd",
    width: "100%",
  };
  const buttonPrimary: React.CSSProperties = {
    padding: "8px 16px",
    borderRadius: 6,
    background: "#4e6e8e",
    color: "#fff",
    fontWeight: 600,
    border: "none",
    cursor: "pointer",
  };
  const buttonSecondary: React.CSSProperties = {
    ...buttonPrimary,
    background: "#e0e6ed",
    color: "#123",
    fontWeight: 500,
  };
  const buttonDanger: React.CSSProperties = {
    ...buttonPrimary,
    background: "#c62828",
  };
  const transferBtn: React.CSSProperties = {
    padding: "6px 12px",
    borderRadius: 6,
    border: "1px solid #cfd8dc",
    background: "#fff",
    cursor: "pointer",
    width: 42,
  };

  // Sort groups by name
  const sortedGroups = React.useMemo(() => {
    return [...groups].sort((a, b) =>
      (a.groupName || "").localeCompare(b.groupName || "", undefined, {
        sensitivity: "base",
      }),
    );
  }, [groups]);

  const memberSet = new Set(form.userIds);
  const members = users.filter((u) => memberSet.has(u.id));
  const available = users.filter((u) => !memberSet.has(u.id));
  const filteredAvailable = available.filter(
    (u) =>
      !filterAvail ||
      u.userName.toLowerCase().includes(filterAvail.toLowerCase()) ||
      (u.userFirstName + " " + u.userLastName)
        .toLowerCase()
        .includes(filterAvail.toLowerCase()),
  );
  const filteredMembers = members.filter(
    (u) =>
      !filterMembers ||
      u.userName.toLowerCase().includes(filterMembers.toLowerCase()) ||
      (u.userFirstName + " " + u.userLastName)
        .toLowerCase()
        .includes(filterMembers.toLowerCase()),
  );
  const docAssignedSet = new Set(form.documentTypeIds);
  const assignedDocTypes = docTypes.filter((dt) => docAssignedSet.has(dt.id));
  const availableDocTypes = docTypes.filter((dt) => !docAssignedSet.has(dt.id));
  const filteredDocAvail = availableDocTypes.filter(
    (dt) =>
      !filterDocAvail ||
      dt.documentTypeName?.toLowerCase().includes(filterDocAvail.toLowerCase()),
  );
  const filteredDocAssigned = assignedDocTypes.filter(
    (dt) =>
      !filterDocAssigned ||
      dt.documentTypeName
        ?.toLowerCase()
        .includes(filterDocAssigned.toLowerCase()),
  );
  function toggleListSelection(
    id: number,
    list: "avail" | "members",
    multi: boolean,
  ) {
    if (list === "avail")
      setSelectedAvailable((prev) =>
        multi
          ? prev.includes(id)
            ? prev.filter((x) => x !== id)
            : [...prev, id]
          : prev.includes(id) && prev.length === 1
            ? []
            : [id],
      );
    else
      setSelectedMembers((prev) =>
        multi
          ? prev.includes(id)
            ? prev.filter((x) => x !== id)
            : [...prev, id]
          : prev.includes(id) && prev.length === 1
            ? []
            : [id],
      );
  }
  function toggleDocSelection(
    id: number,
    list: "avail" | "assigned",
    multi: boolean,
  ) {
    if (list === "avail")
      setSelectedDocAvail((prev) =>
        multi
          ? prev.includes(id)
            ? prev.filter((x) => x !== id)
            : [...prev, id]
          : prev.includes(id) && prev.length === 1
            ? []
            : [id],
      );
    else
      setSelectedDocAssigned((prev) =>
        multi
          ? prev.includes(id)
            ? prev.filter((x) => x !== id)
            : [...prev, id]
          : prev.includes(id) && prev.length === 1
            ? []
            : [id],
      );
  }

  return (
    <div
      style={{
        display: "flex",
        height: "calc(90vh / var(--ui-scale))",
        minHeight: "calc(90vh / var(--ui-scale))",
        border: "1px solid #ccc",
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: 420,
          borderRight: "1px solid #ccc",
          display: "flex",
          flexDirection: "column",
          background: "#f7fafd",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            borderBottom: "1px solid #e0e6ed",
          }}
        >
          <h3 style={{ margin: 0, fontWeight: 600 }}>Groups</h3>
          <button
            onClick={handleAdd}
            title="Add group"
            style={{
              background: "#4e6e8e",
              border: "none",
              width: 36,
              height: 36,
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </div>
        {loading && <div style={{ padding: "8px 16px" }}>Loading...</div>}
        {error && (
          <div style={{ padding: "8px 16px", color: "red" }}>{error}</div>
        )}
        <div style={{ overflowY: "auto", flex: 1 }}>
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}
          >
            <thead>
              <tr style={{ background: "#eaf1f7" }}>
                <th
                  style={{
                    textAlign: "left",
                    padding: "6px 10px",
                    borderBottom: "1px solid #e0e6ed",
                  }}
                >
                  Group
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedGroups.map((g) => {
                const sel = g.id === selectedGroupId;
                const textStyle: React.CSSProperties = {
                  fontWeight: g.groupIsDomain ? 700 : 400,
                };
                return (
                  <tr
                    key={g.id}
                    onClick={() => handleSelect(g.id)}
                    style={{
                      cursor: "pointer",
                      background: sel ? "#d8ecff" : "transparent",
                    }}
                  >
                    <td
                      style={{
                        padding: "6px 10px",
                        borderBottom: "1px solid #f0f4f7",
                        ...textStyle,
                      }}
                    >
                      {g.groupName}
                    </td>
                  </tr>
                );
              })}
              {sortedGroups.length === 0 && !loading && (
                <tr>
                  <td
                    colSpan={2}
                    style={{ padding: 16, textAlign: "center", color: "#888" }}
                  >
                    No groups
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          padding: "16px 24px",
        }}
      >
        {editing ? (
          <div style={{ maxWidth: 1600 }}>
            <h3 style={{ marginTop: 0 }}>
              {selectedGroupId == null ? "Add Group" : "Edit Group"}
            </h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSave();
              }}
              style={{ display: "flex", flexDirection: "column", gap: 16 }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "180px 1fr 180px 1fr",
                  gap: 12,
                  background: "#fff",
                  padding: 20,
                  borderRadius: 12,
                  border: "1px solid #e0e6ed",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                }}
              >
                <label style={{ alignSelf: "center", fontWeight: 500 }}>
                  Name
                </label>
                <input
                  name="groupName"
                  value={form.groupName}
                  onChange={handleFormChange}
                  required
                  style={inputStyle}
                />
                <label style={{ alignSelf: "center", fontWeight: 500 }}>
                  Description
                </label>
                <input
                  name="groupDescription"
                  value={form.groupDescription}
                  onChange={handleFormChange}
                  style={inputStyle}
                />
                <label style={{ alignSelf: "center", fontWeight: 500 }}>
                  Domain Group
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    name="groupIsDomain"
                    checked={form.groupIsDomain}
                    onChange={handleFormChange}
                    style={{ zoom: 1.2 }}
                  />
                  <span style={{ fontSize: 12, color: "#555" }}>
                    Membership may be auto-managed
                  </span>
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 24,
                }}
              >
                <div
                  style={{
                    background: "#fff",
                    padding: 20,
                    borderRadius: 12,
                    border: "1px solid #e0e6ed",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                  }}
                >
                  <h4 style={{ margin: "0 0 12px 0" }}>Users</h4>
                  <div style={{ display: "flex", gap: 24 }}>
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          marginBottom: 6,
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span>Available ({available.length})</span>
                        <input
                          placeholder="filter"
                          value={filterAvail}
                          onChange={(e) => setFilterAvail(e.target.value)}
                          style={{
                            ...inputStyle,
                            width: 140,
                            padding: "2px 6px",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          flex: 1,
                          border: "1px solid #cfd8dc",
                          borderRadius: 8,
                          overflow: "hidden",
                          background: "#f7fafd",
                        }}
                      >
                        <ul
                          style={{
                            listStyle: "none",
                            margin: 0,
                            padding: 0,
                            maxHeight: 240,
                            overflowY: "auto",
                          }}
                        >
                          {filteredAvailable.map((u) => {
                            const sel = selectedAvailable.includes(u.id);
                            return (
                              <li
                                key={u.id}
                                onClick={(e) =>
                                  toggleListSelection(
                                    u.id,
                                    "avail",
                                    e.ctrlKey || e.metaKey,
                                  )
                                }
                                onDoubleClick={moveSelectedToMembers}
                                style={{
                                  padding: "6px 10px",
                                  cursor: "pointer",
                                  background: sel ? "#d8ecff" : "transparent",
                                  borderBottom: "1px solid #e0e6ed",
                                  userSelect: "none",
                                }}
                              >
                                {u.userName}
                              </li>
                            );
                          })}
                          {filteredAvailable.length === 0 && (
                            <li style={{ padding: 10, color: "#888" }}>
                              No users
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        justifyContent: "center",
                      }}
                    >
                      <button
                        type="button"
                        style={transferBtn}
                        onClick={moveSelectedToMembers}
                        disabled={!selectedAvailable.length}
                      >
                        {">"}
                      </button>
                      <button
                        type="button"
                        style={transferBtn}
                        onClick={moveAllToMembers}
                        disabled={!available.length}
                      >
                        {">>"}
                      </button>
                      <button
                        type="button"
                        style={transferBtn}
                        onClick={moveSelectedToAvailable}
                        disabled={!selectedMembers.length}
                      >
                        {"<"}
                      </button>
                      <button
                        type="button"
                        style={transferBtn}
                        onClick={moveAllToAvailable}
                        disabled={!form.userIds.length}
                      >
                        {"<<"}
                      </button>
                    </div>
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          marginBottom: 6,
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span>Members ({members.length})</span>
                        <input
                          placeholder="filter"
                          value={filterMembers}
                          onChange={(e) => setFilterMembers(e.target.value)}
                          style={{
                            ...inputStyle,
                            width: 140,
                            padding: "2px 6px",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          flex: 1,
                          border: "1px solid #cfd8dc",
                          borderRadius: 8,
                          overflow: "hidden",
                          background: "#f7fafd",
                        }}
                      >
                        <ul
                          style={{
                            listStyle: "none",
                            margin: 0,
                            padding: 0,
                            maxHeight: 240,
                            overflowY: "auto",
                          }}
                        >
                          {filteredMembers.map((u) => {
                            const sel = selectedMembers.includes(u.id);
                            return (
                              <li
                                key={u.id}
                                onClick={(e) =>
                                  toggleListSelection(
                                    u.id,
                                    "members",
                                    e.ctrlKey || e.metaKey,
                                  )
                                }
                                onDoubleClick={moveSelectedToAvailable}
                                style={{
                                  padding: "6px 10px",
                                  cursor: "pointer",
                                  background: sel ? "#d8ecff" : "transparent",
                                  borderBottom: "1px solid #e0e6ed",
                                  userSelect: "none",
                                }}
                              >
                                {u.userName}
                              </li>
                            );
                          })}
                          {filteredMembers.length === 0 && (
                            <li style={{ padding: 10, color: "#888" }}>
                              No members
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    background: "#fff",
                    padding: 20,
                    borderRadius: 12,
                    border: "1px solid #e0e6ed",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                  }}
                >
                  <h4 style={{ margin: "0 0 12px 0" }}>Document Types</h4>
                  <div style={{ display: "flex", gap: 24 }}>
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          marginBottom: 6,
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span>Available ({availableDocTypes.length})</span>
                        <input
                          placeholder="filter"
                          value={filterDocAvail}
                          onChange={(e) => setFilterDocAvail(e.target.value)}
                          style={{
                            ...inputStyle,
                            width: 140,
                            padding: "2px 6px",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          flex: 1,
                          border: "1px solid #cfd8dc",
                          borderRadius: 8,
                          overflow: "hidden",
                          background: "#f7fafd",
                        }}
                      >
                        <ul
                          style={{
                            listStyle: "none",
                            margin: 0,
                            padding: 0,
                            maxHeight: 240,
                            overflowY: "auto",
                          }}
                        >
                          {filteredDocAvail.map((dt) => {
                            const sel = selectedDocAvail.includes(dt.id);
                            return (
                              <li
                                key={dt.id}
                                onClick={(e) =>
                                  toggleDocSelection(
                                    dt.id,
                                    "avail",
                                    e.ctrlKey || e.metaKey,
                                  )
                                }
                                onDoubleClick={moveDocSelectedToAssigned}
                                style={{
                                  padding: "6px 10px",
                                  cursor: "pointer",
                                  background: sel ? "#d8ecff" : "transparent",
                                  borderBottom: "1px solid #e0e6ed",
                                  userSelect: "none",
                                }}
                              >
                                {dt.documentTypeName}
                              </li>
                            );
                          })}
                          {filteredDocAvail.length === 0 && (
                            <li style={{ padding: 10, color: "#888" }}>
                              No types
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        justifyContent: "center",
                      }}
                    >
                      <button
                        type="button"
                        style={transferBtn}
                        onClick={moveDocSelectedToAssigned}
                        disabled={!selectedDocAvail.length}
                      >
                        {">"}
                      </button>
                      <button
                        type="button"
                        style={transferBtn}
                        onClick={moveDocAllToAssigned}
                        disabled={!availableDocTypes.length}
                      >
                        {">>"}
                      </button>
                      <button
                        type="button"
                        style={transferBtn}
                        onClick={moveDocSelectedToAvailable}
                        disabled={!selectedDocAssigned.length}
                      >
                        {"<"}
                      </button>
                      <button
                        type="button"
                        style={transferBtn}
                        onClick={moveDocAllToAvailable}
                        disabled={!form.documentTypeIds.length}
                      >
                        {"<<"}
                      </button>
                    </div>
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          marginBottom: 6,
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <span>Assigned ({assignedDocTypes.length})</span>
                        <input
                          placeholder="filter"
                          value={filterDocAssigned}
                          onChange={(e) => setFilterDocAssigned(e.target.value)}
                          style={{
                            ...inputStyle,
                            width: 140,
                            padding: "2px 6px",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          flex: 1,
                          border: "1px solid #cfd8dc",
                          borderRadius: 8,
                          overflow: "hidden",
                          background: "#f7fafd",
                        }}
                      >
                        <ul
                          style={{
                            listStyle: "none",
                            margin: 0,
                            padding: 0,
                            maxHeight: 240,
                            overflowY: "auto",
                          }}
                        >
                          {filteredDocAssigned.map((dt) => {
                            const sel = selectedDocAssigned.includes(dt.id);
                            return (
                              <li
                                key={dt.id}
                                onClick={(e) =>
                                  toggleDocSelection(
                                    dt.id,
                                    "assigned",
                                    e.ctrlKey || e.metaKey,
                                  )
                                }
                                onDoubleClick={moveDocSelectedToAvailable}
                                style={{
                                  padding: "6px 10px",
                                  cursor: "pointer",
                                  background: sel ? "#d8ecff" : "transparent",
                                  borderBottom: "1px solid #e0e6ed",
                                  userSelect: "none",
                                }}
                              >
                                {dt.documentTypeName}
                              </li>
                            );
                          })}
                          {filteredDocAssigned.length === 0 && (
                            <li style={{ padding: 10, color: "#888" }}>
                              No assigned
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <button type="submit" style={buttonPrimary} disabled={loading}>
                  Save
                </button>
                <button
                  type="button"
                  style={buttonSecondary}
                  onClick={() => setEditing(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        ) : selectedGroup ? (
          <div style={{ maxWidth: 1100 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <h3 style={{ margin: 0 }}>Group Details</h3>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={handleEdit}
                  style={buttonSecondary}
                  disabled={loading}
                >
                  Edit
                </button>
                <button
                  onClick={handleDelete}
                  style={buttonDanger}
                  disabled={loading}
                >
                  Delete
                </button>
              </div>
            </div>
            <div
              style={{
                background: "#fff",
                padding: 20,
                borderRadius: 12,
                border: "1px solid #e0e6ed",
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                display: "grid",
                gridTemplateColumns: "180px 1fr",
                rowGap: 8,
              }}
            >
              <div style={{ fontWeight: 500 }}>Name:</div>
              <div>{selectedGroup.groupName}</div>
              <div style={{ fontWeight: 500 }}>Description:</div>
              <div>{selectedGroup.groupDescription}</div>
              <div style={{ fontWeight: 500 }}>Domain Group:</div>
              <div>{selectedGroup.groupIsDomain ? "Yes" : "No"}</div>
              <div style={{ fontWeight: 500 }}>Members:</div>
              <div>
                {users
                  .filter((u) => u.userGroups?.includes(selectedGroup.id))
                  .map((u) => u.userName)
                  .join(", ") || "(none)"}
              </div>
              <div style={{ fontWeight: 500 }}>Document Types:</div>
              <div>
                {groupPermissions
                  .filter((p) => p.objectTypeId === OBJECTTYPE_DOCUMENTTYPE)
                  .map(
                    (p) =>
                      docTypes.find((d) => d.id === p.objectId)
                        ?.documentTypeName,
                  )
                  .filter(Boolean)
                  .join(", ") || "(none)"}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ color: "#567", fontSize: 16, paddingTop: 12 }}>
            Select a group from the list or click the + icon to add a new group.
          </div>
        )}
      </div>
    </div>
  );
}
